import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getRoadblockDetail,
    getRoadblockTypes,
    updateRoadblock,
    addRoadblockNote,
    deleteRoadblock,
    FieldValidationError,
} from '../roadblockService';
import { listDFNs } from '../../../DFN/dfnService';
import { getAllMembers } from '../../../common/apiClient';
import { getEligibleMemberOptions, PROJECT_ID_REGEX, PROJECT_ID_ERROR } from '../constants';
import {
    isICCSupport,
    canEditAll,
    getUserProfileInfo,
    getAssociatedTeams,
    isReadOnly,
} from '../../../auth';
import { formatDateToYyyyMmDd } from '../../../../utils/formatDate';

const MAX_NOTE = 500;

const buildEditForm = (detail) => !detail ? {} : ({
    date: detail.date ? new Date(detail.date) : null,
    productionImpact: detail.productionImpact,
    leaderTimeSpent: detail.leaderTimeSpent || '',
    leaderTimeUnit: detail.leaderTimeUnit || 'hours',
    dfn: detail.dfn ?? null,
    assignedTo: detail.assignedTo ?? null,
    roadblockSubtype: detail.roadblockSubtype ?? null,
    projectId: detail.projectId || '',
    neighborhood: detail.neighborhood || '',
});

export function useRoadblockDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [roadblock, setRoadblock] = useState(null);
    const [typeOptions, setTypeOptions] = useState([]);
    const [dfnOptions, setDfnOptions] = useState([]);
    const [allMembers, setAllMembers] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [inputErrors, setInputErrors] = useState({});
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [noteError, setNoteError] = useState('');

    const memberOptions = useMemo(
        () => getEligibleMemberOptions(allMembers, roadblock?.team),
        [allMembers, roadblock?.team]
    );

    const subtypeOptions = useMemo(() => {
        const typeObj = typeOptions.find(t => t.value === roadblock?.roadblockType);
        return (typeObj?.subtypes || []).map(st => ({ key: st.id, value: st.id, text: st.label }));
    }, [typeOptions, roadblock?.roadblockType]);

    const loadDetail = useCallback(async () => {
        setLoaded(false);
        setError('');
        try {
            const [detail, types, dfnsResp, members] = await Promise.all([
                getRoadblockDetail(id),
                getRoadblockTypes(),
                listDFNs(),
                getAllMembers(),
            ]);
            const dfns = await dfnsResp.json();
            setDfnOptions(dfns.map(d => ({ key: d.id, value: d.id, text: d.name })));
            setAllMembers(members);
            setRoadblock(detail);
            setTypeOptions(types.map(t => ({ key: t.id, value: t.id, text: t.label, requiresDescription: t.requiresDescription, subtypes: t.subtypes || [] })));
            setEditForm(buildEditForm(detail));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }, [id]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    const handleEditChange = (key, value) => {
        setEditForm(prev => ({ ...prev, [key]: value }));
        if (inputErrors[key]) setInputErrors(prev => ({ ...prev, [key]: null }));
    };

    const handleSave = async () => {
        const dateIsInvalid = !editForm.date || isNaN(editForm.date.getTime());
        if (dateIsInvalid) {
            setInputErrors({ date: 'Date is required.' });
            return;
        }
        const projectId = editForm.projectId?.trim() ?? '';
        if (projectId && !PROJECT_ID_REGEX.test(projectId)) {
            setInputErrors({ projectId: PROJECT_ID_ERROR });
            return;
        }
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const updated = await updateRoadblock(id, {
                date: formatDateToYyyyMmDd(editForm.date),
                production_impact: editForm.productionImpact,
                leader_time_spent: editForm.leaderTimeSpent || null,
                leader_time_unit: editForm.leaderTimeUnit || 'hours',
                dfn: editForm.dfn,
                assigned_to: editForm.assignedTo,
                roadblock_subtype: editForm.roadblockSubtype || null,
                project_id: editForm.projectId?.trim() || null,
                neighborhood: editForm.neighborhood?.trim() || null,
            });
            setRoadblock(updated);
            setIsEditing(false);
            setSuccess('Changes saved successfully.');
        } catch (err) {
            if (err instanceof FieldValidationError) {
                setInputErrors(err.fields);
            } else {
                setError(err.message);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setInputErrors({});
        setError('');
        if (roadblock) setEditForm(buildEditForm(roadblock));
    };

    const handleAddNote = async () => {
        if (!noteText.trim()) return;
        setSavingNote(true);
        setNoteError('');
        try {
            const newNote = await addRoadblockNote(id, noteText.trim());
            setRoadblock(prev => ({ ...prev, notes: [...(prev.notes || []), newNote] }));
            setNoteText('');
        } catch (err) {
            setNoteError(err.message);
        } finally {
            setSavingNote(false);
        }
    };

    const handleResolved = () => {
        setShowResolveModal(false);
        navigate('/forms/roadblock');
    };

    // Client-side visibility gate — server enforces the real check.
    // Allowed: ICC Support, superusers (allowEditsAll), and Managers on the same team.
    const canDelete = (rb) => {
        if (!rb) return false;
        if (isReadOnly()) return false;
        if (isICCSupport()) return true;
        if (canEditAll()) return true;
        const user = getUserProfileInfo();
        if (user?.role === 'Manager' && getAssociatedTeams().some(t => t.name === rb.teamName)) return true;
        return false;
    };

    const handleDelete = async () => {
        setDeleting(true);
        setError('');
        try {
            await deleteRoadblock(id);
            navigate('/forms/roadblock');
        } catch (err) {
            setError(err.message);
            setDeleting(false);
        }
    };

    return {
        id,
        roadblock,
        typeOptions,
        dfnOptions,
        memberOptions,
        subtypeOptions,
        loaded,
        isEditing,
        setIsEditing,
        editForm,
        saving,
        error,
        setError,
        success,
        setSuccess,
        inputErrors,
        showResolveModal,
        setShowResolveModal,
        showDeleteModal,
        setShowDeleteModal,
        deleting,
        noteText,
        setNoteText,
        savingNote,
        noteError,
        maxNote: MAX_NOTE,
        handleEditChange,
        handleSave,
        handleCancelEdit,
        handleAddNote,
        handleResolved,
        canDelete,
        handleDelete,
    };
}
